import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useMutation } from "@urql/next";
import { FragmentOf, graphql, readFragment } from "gql.tada";
import { Loader2Icon, UsersIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { formatChatDate } from "@/lib/ai/format-chat-date";
import { UpdateAgentChat } from "@/lib/ai/mutation";
import { cn } from "@/lib/utils";

const ChatMenu = dynamic(
  () => import("../chat-menu").then((m) => ({ default: m.ChatMenu })),
  { ssr: false },
);

/**
 * チャット履歴一覧のアイテムに必要なデータ
 */
export const chatListItemFragment = graphql(`
  fragment ChatListItem on AgentChat {
    id
    title
    shared
    createdAt
    updatedAt
    createdBy {
      id
      name
      email
      picture
    }
  }`);

type Props = {
  chat: FragmentOf<typeof chatListItemFragment>;
  onDeleted: (chatId: string) => void;
  onRenamed: (chatId: string, title: string) => void;
  isSelected: boolean;
  dateField: "createdAt" | "updatedAt";
};

/**
 * チャット履歴一覧のアイテム
 */
export function ChatListItem({
  chat: chatFromProps,
  onDeleted,
  onRenamed,
  isSelected,
  dateField,
}: Props) {
  const chat = readFragment(chatListItemFragment, chatFromProps);
  const { chatLinkComponent: ChatLink, lang } = useAiChatUiContext();
  const dateValue = chat[dateField];
  const formattedDate = dateValue
    ? formatChatDate(dateValue, lang ?? "ja")
    : "";

  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(chat.title ?? "");

  const [{ fetching: renaming }, updateChat] = useMutation(UpdateAgentChat);

  const { i18n } = useLingui();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasCancelledRenameRef = useRef(false);
  const focusInput = () => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const handleRenameRequest = () => {
    hasCancelledRenameRef.current = false;
    setDraftTitle(chat.title ?? "");
    setIsEditing(true);
    focusInput();
  };

  const handleRenameKeydown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleRenameSubmit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      handleRenameCancel();
    }
  };

  const handleRenameCancel = () => {
    hasCancelledRenameRef.current = true;
    setIsEditing(false);
    setDraftTitle(chat.title ?? "");
  };

  const handleRenameBlur = () => {
    if (hasCancelledRenameRef.current) {
      return;
    }

    handleRenameSubmit();
  };

  const handleRenameSubmit = async () => {
    if (renaming) return;

    const result = await updateChat({
      input: {
        id: chat.id,
        title: draftTitle,
      },
    });

    if (result.error) {
      toast.error(i18n._(msg`Failed to rename chat`), {
        description: result.error.message,
      });
      return;
    }

    setIsEditing(false);
    onRenamed?.(chat.id, draftTitle);
  };

  return (
    <div className="group relative">
      {isEditing ? (
        <div
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2 py-1",
            isSelected ? "bg-primary/10 text-primary" : "bg-muted/40",
          )}
        >
          <Input
            ref={(input) => {
              inputRef.current = input;
            }}
            value={draftTitle}
            onChange={(event) => setDraftTitle(event.target.value)}
            onKeyDown={handleRenameKeydown}
            onBlur={handleRenameBlur}
            placeholder={i18n._(msg`Enter chat title`)}
            className="flex-1 border-0 bg-transparent px-1 pt-0.5 text-sm h-6 focus-visible:ring-0 focus-visible:ring-offset-0 font-medium shadow-none"
          />
          {renaming ? (
            <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : null}
        </div>
      ) : (
        <Button
          asChild
          variant="ghost"
          className={cn(
            "h-auto w-full justify-start rounded-md px-3 py-1.5",
            isSelected
              ? "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary"
              : "text-foreground hover:bg-muted",
          )}
        >
          <ChatLink chatId={chat.id} isSelected={isSelected}>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex items-center gap-1">
                <span className="truncate text-left text-sm font-medium text-foreground/70">
                  {chat.title || <Trans>(No title)</Trans>}
                </span>
                {chat.shared ? (
                  <UsersIcon
                    className="size-3.5 text-muted-foreground shrink-0"
                    fill="currentColor"
                    strokeWidth={2}
                    aria-label={i18n.t("Shared by {name}", {
                      name: chat.createdBy.name,
                    })}
                  />
                ) : null}
              </span>
              {formattedDate ? (
                <span className="text-xs text-muted-foreground font-normal">
                  {formattedDate}
                </span>
              ) : null}
            </span>
          </ChatLink>
        </Button>
      )}
      {isEditing ? null : (
        <ChatMenu
          chatId={chat.id}
          onDeleted={() => onDeleted(chat.id)}
          onRenameRequest={(_title) => handleRenameRequest()}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
        />
      )}
    </div>
  );
}
